/**
 * <variant-view-switcher>
 * Wraps a single product option's swatch grid and adds a Grid / List toggle.
 * In list view the swatch grid is hidden and a custom dropdown is shown, where
 * each row is: [color circle] Title - shade description.
 *
 * Selecting a row checks the matching radio input and dispatches a bubbling
 * `change` event so the existing <variant-radios> logic (price, media, URL,
 * add-to-cart) runs exactly as it does for the grid swatches.
 */
(function () {
  if (window.customElements.get('variant-view-switcher')) return;

  // List-view height rules (see applyPanelHeight).
  const FIT_MAX_ROWS = 2;   // up to this many rows the list just fits, no scroll
  const PEEK_MAX_ROWS = 6;  // up to this many rows: all but the last + a slice
  const PEEK_RATIO = 0.45;  // how much of that last row stays visible

  class VariantViewSwitcher extends HTMLElement {
    connectedCallback() {
      // When this element's definition loads before its markup, the parser
      // fires connectedCallback at the start tag — before the toggle/dropdown
      // children exist. Wait for the full document parse in that case.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;

      this.toggle = this.querySelector('[data-variant-view-toggle]');
      this.dropdown = this.querySelector('[data-variant-dropdown]');

      // Non-swatch options only have the grid — nothing to wire up.
      if (!this.toggle || !this.dropdown) return;

      this.initialized = true;

      this.grid = this.querySelector('[data-swatch-grid]');
      this.viewButtons = Array.from(this.toggle.querySelectorAll('[data-view]'));
      this.trigger = this.dropdown.querySelector('[data-variant-dropdown-trigger]');
      this.panel = this.dropdown.querySelector('[data-variant-dropdown-panel]');
      this.current = this.dropdown.querySelector('[data-variant-dropdown-current]');
      this.options = Array.from(this.dropdown.querySelectorAll('[data-variant-dropdown-option]'));

      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.bindToggle();
      this.bindDropdown();
      this.syncCurrent();

      // Keep the dropdown's selected state in sync when a grid swatch is picked.
      this.addEventListener('change', (e) => {
        if (e.target && e.target.matches('input[type="radio"]')) {
          this.syncCurrent();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    bindToggle() {
      this.viewButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.setView(btn.dataset.view));
      });
    }

    setView(view) {
      const isList = view === 'list';
      const entering = isList && !this.classList.contains('is-list-view');

      // The grid box is only measurable while the grid is still on screen, so
      // grab it before the class flip; the rows are only measurable after it.
      const gridBoxHeight = entering ? this.measureGridBox() : 0;

      this.classList.toggle('is-list-view', isList);
      this.viewButtons.forEach((btn) => {
        const active = btn.dataset.view === view;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // In list view the panel is shown inline (no open/close); in grid view it
      // collapses back to a hidden dropdown panel.
      if (isList) {
        this.panel.hidden = false;
        if (entering) this.applyPanelHeight(gridBoxHeight);
        this.scrollSelectedIntoView();
      } else {
        this.closePanel();
      }
    }

    /**
     * How tall the inline list should be, driven by the number of rows:
     *
     *   <= FIT_MAX_ROWS   fit the content exactly — never scroll a 2-shade list
     *   <= PEEK_MAX_ROWS  every row but the last, plus a slice of the last one,
     *                     so the cut row reads as "there is more below"
     *   more than that    fall back to the grid box height, which keeps the page
     *                     from jumping when toggling Grid / List
     *
     * Rows are measured individually rather than multiplied out, because a long
     * shade description can wrap onto a second line.
     */
    applyPanelHeight(gridBoxHeight) {
      const rows = this.options;
      const count = rows.length;
      if (!count) return;

      if (count <= FIT_MAX_ROWS) {
        this.panel.style.height = '';
        return;
      }

      if (count <= PEEK_MAX_ROWS) {
        let height = 0;
        for (let i = 0; i < count - 1; i++) height += rows[i].offsetHeight;
        height += rows[count - 1].offsetHeight * PEEK_RATIO;
        this.panel.style.height = Math.round(height + this.panelChrome()) + 'px';
        return;
      }

      if (gridBoxHeight > 0) this.panel.style.height = gridBoxHeight + 'px';
    }

    // Row offsetHeight covers the row only, and the panel is border-box in list
    // view, so its own border + padding has to be added on top.
    panelChrome() {
      const s = getComputedStyle(this.panel);
      return (
        parseFloat(s.borderTopWidth) +
        parseFloat(s.borderBottomWidth) +
        parseFloat(s.paddingTop) +
        parseFloat(s.paddingBottom)
      );
    }

    scrollSelectedIntoView() {
      const sel = this.panel.querySelector('[data-variant-dropdown-option].is-selected');
      if (!sel) return;
      // offsetTop is relative to the shared positioned ancestor, so subtracting
      // the panel's keeps the page from scrolling — only the list scrolls.
      const offset = sel.offsetTop - this.panel.offsetTop;
      this.panel.scrollTop = offset - (this.panel.clientHeight - sel.offsetHeight) / 2;
    }

    measureGridBox() {
      if (!this.grid) return 0;
      const gridRect = this.grid.getBoundingClientRect();

      // Span from the top of the swatch grid to the bottom of the variant title
      // line. The matching bottom margin is applied equally to both views in CSS
      // (grid title vs. list dropdown), so it's intentionally excluded here.
      const titleEl = this.nextElementSibling;
      let bottom = gridRect.bottom;
      if (titleEl && titleEl.classList.contains('variant-title-description')) {
        bottom = titleEl.getBoundingClientRect().bottom;
      }

      return Math.round(bottom - gridRect.top);
    }

    bindDropdown() {
      this.trigger.addEventListener('click', () => {
        if (this.panel.hidden) {
          this.openPanel();
        } else {
          this.closePanel();
        }
      });

      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closePanel();
      });

      this.options.forEach((opt) => {
        opt.addEventListener('click', () => this.selectOption(opt));
        opt.addEventListener('keydown', (e) => this.onOptionKeydown(e, opt));
      });

      document.addEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(e) {
      // List view shows the panel inline and never collapses on outside click.
      if (this.classList.contains('is-list-view')) return;
      if (!this.dropdown.contains(e.target)) this.closePanel();
    }

    openPanel() {
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    closePanel() {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    // Listbox keyboard navigation: arrows move focus between options (like the
    // grid's native radio group), Enter / Space selects the focused option.
    onOptionKeydown(e, opt) {
      const key = e.key;

      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        e.preventDefault();
        this.selectOption(opt);
        return;
      }

      const last = this.options.length - 1;
      const idx = this.options.indexOf(opt);
      let next = null;

      if (key === 'ArrowDown' || key === 'ArrowRight') {
        next = this.options[idx + 1] || this.options[0];
      } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
        next = this.options[idx - 1] || this.options[last];
      } else if (key === 'Home') {
        next = this.options[0];
      } else if (key === 'End') {
        next = this.options[last];
      }

      if (next) {
        e.preventDefault();
        // Like the grid's radio group: arrowing onto an option selects it.
        this.selectOption(next);
        next.focus();
      }
    }

    selectOption(opt) {
      const input = document.getElementById(opt.dataset.inputId);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // Keep the inline list open in list view; only the dropdown closes.
      if (!this.classList.contains('is-list-view')) this.closePanel();
      this.syncCurrent();
    }

    syncCurrent() {
      let selected = this.options.find((opt) => {
        const input = document.getElementById(opt.dataset.inputId);
        return input && input.checked;
      });
      if (!selected) selected = this.options[0];
      if (!selected) return;

      this.options.forEach((opt) => {
        const isSel = opt === selected;
        opt.classList.toggle('is-selected', isSel);
        opt.setAttribute('aria-selected', isSel ? 'true' : 'false');
        // Selected row is the single tab stop; arrows reach the rest.
        opt.setAttribute('tabindex', isSel ? '0' : '-1');
      });

      if (this.current) {
        this.current.innerHTML = selected.innerHTML;
      }
    }
  }

  window.customElements.define('variant-view-switcher', VariantViewSwitcher);
})();
